import { mutatingWondersData } from '../data/mutatingWondersData';
import { ExhibitionLayout } from './layouts/ExhibitionLayout';
import { MutatingWondersArtworkCard } from './MutatingWondersArtworkCard';
import VideoBackground from './VideoBackground';
import styled from '@emotion/styled';
import { useState } from 'react';

const ArtworkGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 3rem;
  margin-bottom: 4rem;
  padding: 0 0.5rem;

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 4rem;
    padding: 0 3rem;
  }

  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 5rem;
    padding: 0 2rem;
  }
`;

const ArtworkSection = styled.section`
  margin-bottom: 4rem;
  max-width: 1400px;
  margin: 0 auto 4rem auto;
  
  @media (min-width: 768px) {
    margin-bottom: 6rem;
  }
`;

const IntroSection = styled.section`
  max-width: 1000px;
  margin: 0 auto 6rem auto;
  padding: 0 0.5rem;
  
  @media (min-width: 768px) {
    padding: 0 2rem;
    margin-bottom: 8rem;
  }
`;

const CollapsibleSection = styled.div`
  margin-bottom: 3rem;
`;

const SectionHeader = styled.button`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: none;
  border: none;
  cursor: pointer;
  padding: 1rem 0;
  border-bottom: 2px solid #eee;
  margin-bottom: 1rem;
  
  h2, h3 {
    font-size: 1.2rem;
    font-weight: 600;
    color: #fff;
    margin: 0;
    text-align: left;
    
    @media (min-width: 768px) {
      font-size: 1.4rem;
    }
  }
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
`;

const ExpandIcon = styled.span<{ isExpanded: boolean }>`
  font-size: 1.5rem;
  font-weight: bold;
  transition: transform 0.3s ease;
  transform: ${props => props.isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'};
  color: #666;
`;

const CollapsibleContent = styled.div<{ isExpanded: boolean }>`
  max-height: ${props => props.isExpanded ? 'none' : '0'};
  overflow: hidden;
  transition: all 0.3s ease;
  
  ${props => !props.isExpanded && `
    opacity: 0;
  `}
  
  ${props => props.isExpanded && `
    opacity: 1;
    padding-bottom: 1rem;
  `}
`;

const SectionContent = styled.div`
  h3 {
    font-size: 1.5rem;
    font-weight: 500;
    margin: 2rem 0 1rem 0;
    color: #fff;
    
    @media (min-width: 768px) {
      font-size: 1.75rem;
    }
  }
  
  p {
    font-size: 0.9rem;
    line-height: 1.7;
    margin-bottom: 1.5rem;
    color: #fff;
    text-align: justify;
    
    @media (min-width: 768px) {
      font-size: 1rem;
      line-height: 1.8;
    }
  }
  
  em {
    font-style: italic;
    margin-bottom: 1rem;
    display: block;
    color: #fff;
  }
`;



const MutatingWonders = () => {
  const [biographyExpanded, setBiographyExpanded] = useState(false);
  const [artistStatementExpanded, setArtistStatementExpanded] = useState(false);
  const [curatorNoteExpanded, setCuratorNoteExpanded] = useState(false);

  return (
    <ExhibitionLayout
      title="Mutating Wonders"
      subtitle="A solo exhibition exploring the thematic thread of Seeds, and its fragile state of in-between stages."
      openingDate="JUNE 7, 2025"
      closingDate="AUGUST 17, 2025"
      background={<VideoBackground platform="url" videoId="https://r2.nothingtodo.me/new.mp4" fallbackImage="/images/mutating-wonders/Two Seeds. Rising.png" />}
    >
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 1rem' }}>
        <IntroSection>
          <CollapsibleSection>
            <SectionHeader onClick={() => setBiographyExpanded(!biographyExpanded)}>
              <h2>Biography</h2>
              <ExpandIcon isExpanded={biographyExpanded}>▼</ExpandIcon>
            </SectionHeader>
            <CollapsibleContent isExpanded={biographyExpanded}>
              <SectionContent>
                <p>
                  Mateusz Wiewiorowski has pursued a diverse and international education, studying at institutions such as the Design Academy Eindhoven and Chelsea College of Art and Design in London. In parallel, he has deepened his practical knowledge by working closely with older-generation craftsmen across countries including Norway, Poland, Morocco, Mexico, and Indonesia. These experiences have significantly refined his technical skills in working with materials such as wood, metal, and stone.
                </p>
                <p>
                  His practice is rooted in continuous learning, merging traditional craftsmanship with his own developed techniques and aesthetics. International collaborations have played a crucial role in shaping his artistic development. As a conceptual designer for Azulik in Tulum, Mexico, he explored the integration of art and nature within architectural environments, examining the tension and harmony between the organic and the man-made. His work as a craft producer in Los Angeles with renowned architect Frank Gehry further expanded his creative experience.
                </p>
                <p>
                  In 2022, Wiewiorowski completed his first major sculptural commission for the Petroleum Safety Authority in Stavanger, Norway—marking a significant milestone in his career. In 2024, he founded a workshop and showroom in Lysebåten, located deep in the fjords of Norway's west coast. This space now serves as a creative laboratory for the development of new works and experimentation within sculpture and installation art.
                </p>
                <p>
                  His first solo exhibition, Polar Breakdown, held in Stavanger, Norway, introduced a thematic investigation into the processes of creation and destruction. Through sculpture, he told the story of how human necessity, tools, and craftsmanship shape the material world. A central interest in his work is the fragile balance between creation and decay, a theme he continues to explore across multiple bodies of work.
                </p>
                <p>
                  In the Obsee Eruption collection, Wiewiorowski reflected on the impact of volcanic activity on the Earth's surface, using materials such as obsidian to represent raw natural energy and copper as a symbol of human intervention. This exploration of duality—between nature's unrestrained power and humanity's constructive abilities—has become a hallmark of his artistic inquiry.
                </p>
                <p>
                  Another key project, the Seeds series, features enlarged seed sculptures in wood combined with sculpted and burned copper pipes that evoke organic root systems. By charring the wood and using ash to enrich the color palette, Wiewiorowski explores themes of growth, decay, and regeneration—natural cycles that resonate with broader questions of human innovation and technological development.
                </p>
                <p>
                  His visual language combines geometric abstraction with organic forms, often balancing structure with chaos. Long lines, harmonious proportions, and dynamic shapes characterize his compositions, which frequently rely on contrast—such as black sculptures against white spaces—to shift the viewer's spatial and sensory experience. These explorations of opposites are central to his artistic philosophy.
                </p>
                <p>
                  Wiewiorowski's work is driven by a deep fascination with both human and planetary evolution.
                </p>
              </SectionContent>
            </CollapsibleContent>
          </CollapsibleSection>

          <CollapsibleSection>
            <SectionHeader onClick={() => setArtistStatementExpanded(!artistStatementExpanded)}>
              <h2>Artist Statement & Concept</h2>
              <ExpandIcon isExpanded={artistStatementExpanded}>▼</ExpandIcon>
            </SectionHeader>
            <CollapsibleContent isExpanded={artistStatementExpanded}>
              <SectionContent>
                <p>
                  In Mutating Wonders, I explore transformation not as a destination, but as a living, ongoing process. The sculptures in this series were created during a three-week residency in Chiang Mai, using burnt wood and local materials. They came together quickly and intuitively—raw forms shaped by hand, capturing a moment between structure and collapse, control and release.
                </p>
                <p>
                  This body of work continues my commitment to craft and natural materials—especially wood and copper—which carry the voice of the landscape. My process is always a dialogue: between nature and structure, land and object, time and change.
                </p>
                <p>
                  In Chiang Mai, this dialogue deepened. Surrounded by intense heat, humidity, and vibrant energy, I let the environment shape the work. The result is a series of sculptures that feel alive—mutating, unpredictable, and full of movement.
                </p>
                <p>
                  These works connect to my ongoing Seeds collection, which reflects on growth, evolution, and interconnection. While Seeds focused on beginnings and potential, Mutating Wonders lives in the uncertain middle—the shifting, unstable edge of transformation.
                </p>
                <p>
                  There is power in allowing things to change freely. These sculptures are not fixed—they are snapshots of becoming. Made with local materials and guided by place, they carry both physical and cultural traces of their origin.
                </p>
                <p>
                  At the heart of this exhibition is a question: Can wonder grow? What happens when we stop resisting change and start shaping it with our hands?
                </p>
                <p>
                  The result: organic forms that play with shadow and light—each one a small monument to transformation in motion.
                </p>
              </SectionContent>
            </CollapsibleContent>
          </CollapsibleSection>

          <CollapsibleSection>
            <SectionHeader onClick={() => setCuratorNoteExpanded(!curatorNoteExpanded)}>
              <h2>Curator's Note</h2>
              <ExpandIcon isExpanded={curatorNoteExpanded}>▼</ExpandIcon>
            </SectionHeader>
            <CollapsibleContent isExpanded={curatorNoteExpanded}>
              <SectionContent>
                <p>
                  Upon encountering Mateusz's universe, we were immediately drawn in—what followed was an organic and spontaneous collaboration that brought him into our space as a resident artist. During his time with us, we had the privilege of engaging with his ongoing exploration of Seeds, a thematic thread that continues to evolve in this exhibition, Mutating Wonders. In this exhibition, Mateusz Wiewiorowski invites us into the fragile, transformative states that exist in-between stages. Through the shaping of copper and locally sourced teak wood, he renders these transitional moments tangible—each piece is a meditation on material and metamorphosis.
                </p>
              </SectionContent>
            </CollapsibleContent>
          </CollapsibleSection>
        </IntroSection>

        <ArtworkSection>
          <ArtworkGrid>
            {mutatingWondersData.map(artwork => (
              <MutatingWondersArtworkCard key={artwork.id} artwork={artwork} />
            ))}
          </ArtworkGrid>
        </ArtworkSection>
      </div>
    </ExhibitionLayout>
  );
};

export default MutatingWonders; 